/**
 * Компонент для рендеринга Markdown в сообщениях
 */

import { useMemo, useCallback, AnchorHTMLAttributes } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { ImageGallery } from '../ImageGallery';
import { VideoGallery } from '../VideoGallery';
import { ReviewButtons } from '../ReviewButtons';
import { parseContentSegments } from '@/utils/mediaParser';
import styles from './MarkdownMessage.module.css';

/**
 * Пропсы компонента MarkdownMessage
 */
interface MarkdownMessageProps {
  /** Markdown контент */
  content: string;
}

/**
 * Определяет, ведёт ли href на якорь текущей страницы
 */
function getAnchorId(href: string | undefined): string | null {
  if (!href) return null;

  if (href.startsWith('#')) return href.slice(1);

  try {
    const linkUrl = new URL(href, window.location.origin);
    if (
      linkUrl.origin === window.location.origin &&
      linkUrl.pathname === window.location.pathname &&
      linkUrl.hash
    ) {
      return linkUrl.hash.slice(1);
    }
  } catch {
    // невалидный URL — игнорируем
  }

  return null;
}

/**
 * Рендерит Markdown контент с сохранением порядка текста и медиа.
 * Якорные ссылки скроллят к элементу, остальные — открываются в новой вкладке.
 */
export function MarkdownMessage({ content }: MarkdownMessageProps) {
  const segments = useMemo(() => parseContentSegments(content), [content]);

  const AnchorLink = useCallback(
    (props: AnchorHTMLAttributes<HTMLAnchorElement>) => {
      const anchorId = getAnchorId(props.href);

      if (anchorId) {
        return (
          <a
            {...props}
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById(anchorId) || document.querySelector(`[name="${anchorId}"]`);
              el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          >
            {props.children}
          </a>
        );
      }

      return (
        <a {...props} target="_blank" rel="noopener noreferrer">
          {props.children}
        </a>
      );
    },
    [],
  );

  const markdownComponents = useMemo(() => ({ a: AnchorLink }), [AnchorLink]);

  const sanitizeSchema = useMemo(() => ({
    ...defaultSchema,
    tagNames: [...(defaultSchema.tagNames || []), 'br', 'p', 'strong', 'em', 'u', 'del', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'a', 'code', 'pre', 'blockquote', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr', 'sup', 'sub', 'span', 'div'],
    attributes: {
      ...defaultSchema.attributes,
      a: ['href', 'title', 'target', 'rel'],
      span: ['className'],
      div: ['className'],
      code: ['className'],
      td: ['align'],
      th: ['align'],
    },
  }), []);

  return (
    <div className={styles.markdown}>
      {segments.map((seg, i) => {
        if (seg.type === 'text') {
          return (
            <ReactMarkdown
              key={i}
              remarkPlugins={[remarkGfm, remarkBreaks]}
              rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
              components={markdownComponents}
            >
              {seg.content}
            </ReactMarkdown>
          );
        }
        if (seg.type === 'image') {
          return <ImageGallery key={i} images={seg.images} />;
        }
        if (seg.type === 'review') {
          return <ReviewButtons key={i} kitCode={seg.kitCode} />;
        }
        if (seg.type === 'video') {
          return <VideoGallery key={i} videos={seg.videos} />;
        }
        return null;
      })}
    </div>
  );
}

